import { colors, alphaVar } from "../../ui/colors";

export type ContactCenterThemeId = "flownect" | "midnight" | "ocean" | "forest" | "sunset" | "mono";

export interface ContactCenterTheme {
  id: ContactCenterThemeId;
  name: string;
  mode: "light" | "dark";
  swatch: {
    background: string;
    bubbleIn: string;
    bubbleOut: string;
    accent: string;
    header: string;
  };
  shell: {
    bg: string;
    headerBg: string;
    accent: string;
    border: string;
    textPrimary: string;
    textSecondary: string;
  };
  chat: {
    bg: string;
    pattern?: string;
    dividerLine: string;
    dividerText: string;
    scrollThumb: string;
    inputBg: string;
    highlight: string;
  };
  bubbleIn: { bg: string; color: string; border: string };
  bubbleOut: { bg: string; color: string; border: string };
  callButton: { color: string; hoverBg: string };
  link: { color: string; hoverColor: string };
  toast: {
    bg: string;
    border: string;
    accent: string;
    title: string;
    body: string;
    muted: string;
  };
}

const pixelTexture = (color: string) =>
  `repeating-conic-gradient(${color} 0% 25%, transparent 0% 50%) 0 0 / 16px 16px`;

const flownect: ContactCenterTheme = {
  id: "flownect",
  name: "Flownect",
  mode: "light",
  swatch: {
    background: "var(--fx-surface-base, #FFFFFF)",
    bubbleIn: colors.gray[50],
    bubbleOut: colors.orange[50],
    accent: "#E87722",
    header: "#FFFFFF",
  },
  shell: {
    bg: "#FFFFFF",
    headerBg: "#FFFFFF",
    accent: "#E87722",
    border: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.12)",
    textPrimary: "rgba(0,0,0,0.87)",
    textSecondary: "rgba(0,0,0,0.6)",
  },
  chat: {
    bg: "#FAFAFA",
    dividerLine: "rgba(0,0,0,0.12)",
    dividerText: "rgba(0,0,0,0.38)",
    scrollThumb: "rgba(0,0,0,0.16)",
    inputBg: colors.orange[50],
    highlight: colors.orange[50],
  },
  bubbleIn: { bg: colors.gray[50], color: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.87)", border: colors.gray[100] },
  bubbleOut: { bg: colors.orange[50], color: colors.orange[900], border: colors.orange[100] },
  callButton: { color: colors.orange[500], hoverBg: alphaVar(colors.orange[500], 0.1) },
  link: { color: "#1976d2", hoverColor: "#0d47a1" },
  toast: {
    bg: "#FFFFFF",
    border: alphaVar("var(--fx-surface-borderstrong, #CBD5E1)", 0.18),
    accent: "#ff8a00",
    title: colors.gray[500],
    body: colors.gray[300],
    muted: colors.gray[400],
  },
};

const midnight: ContactCenterTheme = {
  id: "midnight",
  name: "Midnight",
  mode: "dark",
  swatch: {
    background: "#11151c",
    bubbleIn: "#1c2430",
    bubbleOut: "#274060",
    accent: "#6ea8fe",
    header: "#161b23",
  },
  shell: {
    bg: "#161b23",
    headerBg: "#161b23",
    accent: "#6ea8fe",
    border: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.12)",
    textPrimary: "#e6edf3",
    textSecondary: "rgba(230,237,243,0.62)",
  },
  chat: {
    bg: "#11151c",
    pattern: pixelTexture("rgba(255,255,255,0.014)"),
    dividerLine: "rgba(255,255,255,0.12)",
    dividerText: "rgba(230,237,243,0.45)",
    scrollThumb: "rgba(255,255,255,0.2)",
    inputBg: "#1c2430",
    highlight: alphaVar("#6ea8fe", 0.16),
  },
  bubbleIn: { bg: "#1c2430", color: "#e6edf3", border: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.09)" },
  bubbleOut: { bg: alphaVar("#4c7dc4", 0.42), color: "#eaf2ff", border: alphaVar("#6ea8fe", 0.4) },
  callButton: { color: "#6ea8fe", hoverBg: alphaVar("#6ea8fe", 0.14) },
  link: { color: "#6ea8fe", hoverColor: "#a8caff" },
  toast: {
    bg: "#1a212b",
    border: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.14)",
    accent: "#6ea8fe",
    title: "#e6edf3",
    body: "rgba(230,237,243,0.72)",
    muted: "rgba(230,237,243,0.5)",
  },
};

const ocean: ContactCenterTheme = {
  id: "ocean",
  name: "Ocean",
  mode: "light",
  swatch: {
    background: "var(--fx-surface-sunken, #F8FAFC)",
    bubbleIn: alphaVar("#1b6aa5", 0.1),
    bubbleOut: alphaVar("#1b6aa5", 0.24),
    accent: "#1b6aa5",
    header: "#f7fbfe",
  },
  shell: {
    bg: "#f7fbfe",
    headerBg: "#f7fbfe",
    accent: "#1b6aa5",
    border: alphaVar("#1b6aa5", 0.18),
    textPrimary: "#0d2c42",
    textSecondary: alphaVar("#0d2c42", 0.62),
  },
  chat: {
    bg: "#eef5fb",
    dividerLine: alphaVar("#1b6aa5", 0.18),
    dividerText: alphaVar("#0d2c42", 0.45),
    scrollThumb: alphaVar("#1b6aa5", 0.28),
    inputBg: alphaVar("#1b6aa5", 0.08),
    highlight: alphaVar("#1b6aa5", 0.14),
  },
  bubbleIn: { bg: alphaVar("#1b6aa5", 0.1), color: "#0d2c42", border: alphaVar("#1b6aa5", 0.2) },
  bubbleOut: { bg: alphaVar("#1b6aa5", 0.24), color: "#082a41", border: alphaVar("#1b6aa5", 0.42) },
  callButton: { color: "#1b6aa5", hoverBg: alphaVar("#1b6aa5", 0.12) },
  link: { color: "#1565c0", hoverColor: "#0b3f75" },
  toast: {
    bg: "#f7fbfe",
    border: alphaVar("#1b6aa5", 0.22),
    accent: "#1b6aa5",
    title: "#0d2c42",
    body: alphaVar("#0d2c42", 0.72),
    muted: alphaVar("#0d2c42", 0.5),
  },
};

const forest: ContactCenterTheme = {
  id: "forest",
  name: "Forest",
  mode: "light",
  swatch: {
    background: "var(--fx-surface-sunken, #F8FAFC)",
    bubbleIn: alphaVar("#3f6b46", 0.1),
    bubbleOut: alphaVar("#3f6b46", 0.24),
    accent: "#3f6b46",
    header: "#f8faf7",
  },
  shell: {
    bg: "#f8faf7",
    headerBg: "#f8faf7",
    accent: "#3f6b46",
    border: alphaVar("#3f6b46", 0.18),
    textPrimary: "#1e3323",
    textSecondary: alphaVar("#1e3323", 0.62),
  },
  chat: {
    bg: "#f1f5ef",
    pattern: pixelTexture(alphaVar("#3f6b46", 0.03)),
    dividerLine: alphaVar("#3f6b46", 0.18),
    dividerText: alphaVar("#1e3323", 0.45),
    scrollThumb: alphaVar("#3f6b46", 0.28),
    inputBg: alphaVar("#3f6b46", 0.08),
    highlight: alphaVar("#3f6b46", 0.14),
  },
  bubbleIn: { bg: alphaVar("#3f6b46", 0.1), color: "#1e3323", border: alphaVar("#3f6b46", 0.2) },
  bubbleOut: { bg: alphaVar("#3f6b46", 0.24), color: "#152a1a", border: alphaVar("#3f6b46", 0.42) },
  callButton: { color: "#3f6b46", hoverBg: alphaVar("#3f6b46", 0.12) },
  link: { color: "#1565c0", hoverColor: "#0b3f75" },
  toast: {
    bg: "#f8faf7",
    border: alphaVar("#3f6b46", 0.22),
    accent: "#3f6b46",
    title: "#1e3323",
    body: alphaVar("#1e3323", 0.72),
    muted: alphaVar("#1e3323", 0.5),
  },
};

const sunset: ContactCenterTheme = {
  id: "sunset",
  name: "Sunset",
  mode: "light",
  swatch: {
    background: "var(--fx-surface-sunken, #F8FAFC)",
    bubbleIn: alphaVar("#8b3fa5", 0.09),
    bubbleOut: alphaVar("#d1477a", 0.22),
    accent: "#8b3fa5",
    header: "#fdf7fc",
  },
  shell: {
    bg: "#fdf7fc",
    headerBg: "#fdf7fc",
    accent: "#8b3fa5",
    border: alphaVar("#8b3fa5", 0.18),
    textPrimary: "#3b1436",
    textSecondary: alphaVar("#3b1436", 0.62),
  },
  chat: {
    bg: "#f8f0f7",
    dividerLine: alphaVar("#8b3fa5", 0.18),
    dividerText: alphaVar("#3b1436", 0.45),
    scrollThumb: alphaVar("#8b3fa5", 0.28),
    inputBg: alphaVar("#8b3fa5", 0.08),
    highlight: alphaVar("#d1477a", 0.14),
  },
  bubbleIn: { bg: alphaVar("#8b3fa5", 0.09), color: "#3b1436", border: alphaVar("#8b3fa5", 0.18) },
  bubbleOut: { bg: alphaVar("#d1477a", 0.22), color: "#4a132f", border: alphaVar("#d1477a", 0.42) },
  callButton: { color: "#d1477a", hoverBg: alphaVar("#d1477a", 0.12) },
  link: { color: "#1565c0", hoverColor: "#0b3f75" },
  toast: {
    bg: "#fdf7fc",
    border: alphaVar("#8b3fa5", 0.22),
    accent: "#8b3fa5",
    title: "#3b1436",
    body: alphaVar("#3b1436", 0.72),
    muted: alphaVar("#3b1436", 0.5),
  },
};

const mono: ContactCenterTheme = {
  id: "mono",
  name: "Mono",
  mode: "light",
  swatch: {
    background: "var(--fx-surface-muted, #F1F5F9)",
    bubbleIn: "#e7e7e9",
    bubbleOut: "#d4d4d8",
    accent: "#3f3f46",
    header: "#fafafa",
  },
  shell: {
    bg: "#fafafa",
    headerBg: "#fafafa",
    accent: "#3f3f46",
    border: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.14)",
    textPrimary: "#18181b",
    textSecondary: "rgba(24,24,27,0.62)",
  },
  chat: {
    bg: "#f4f4f5",
    dividerLine: "rgba(0,0,0,0.14)",
    dividerText: "rgba(24,24,27,0.45)",
    scrollThumb: "rgba(0,0,0,0.22)",
    inputBg: "#ececee",
    highlight: "rgba(0,0,0,0.07)",
  },
  bubbleIn: { bg: "#e7e7e9", color: "var(--fx-surface-text, #0F172A)", border: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.1)" },
  bubbleOut: { bg: "#d4d4d8", color: "var(--fx-surface-text, #0F172A)", border: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.16)" },
  callButton: { color: "var(--fx-surface-textsecondary, #64748B)", hoverBg: "rgba(63,63,70,0.1)" },
  link: { color: "#1565c0", hoverColor: "#0b3f75" },
  toast: {
    bg: "#fafafa",
    border: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.18)",
    accent: "#3f3f46",
    title: "#18181b",
    body: "rgba(24,24,27,0.72)",
    muted: "rgba(24,24,27,0.5)",
  },
};

export const CONTACT_CENTER_THEMES: ContactCenterTheme[] = [flownect, midnight, ocean, forest, sunset, mono];

export const DEFAULT_CONTACT_CENTER_THEME_ID: ContactCenterThemeId = "flownect";

export const getContactCenterTheme = (id?: string | null): ContactCenterTheme => {
  return CONTACT_CENTER_THEMES.find((theme) => theme.id === id) ?? flownect;
};
